<template>
    <div class="bonusomat-odometer" ref="bonusomatOdometer">
        <span ref="odometerTensRef"></span>
        <span ref="odometerOnesRef"></span>
        <span class="bonusomat-odometer__letter-odometer odometer-auto-theme">
            <div class="bonusomat-odometer__odometer-inside">
                <span class="odometer-digit">
                    <span class="odometer-digit-inner">
                        <span class="odometer-ribbon">
                            <span class="odometer-ribbon-inner">
                                <span class="bonusomat-odometer__letter" ref="sliderContainer">
                                    <span class="odometer-value odometer-value--unit">s</span>
                                    <span class="odometer-value odometer-value--unit">m</span>
                                    <span class="odometer-value odometer-value--unit">h</span>
                                    <span class="odometer-value odometer-value--unit">d</span>
                                </span>
                            </span>
                        </span>
                    </span>
                </span>
            </div>
        </span>
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";
import Odometer from "odometer";
import { useKeenSlider } from "keen-slider/vue.es";
import { onViewportEnter } from "@sazka/utils/viewport-enter";

const props = defineProps<{
    remainingSeconds?: number;
}>();

const bonusomatOdometer = ref<HTMLElement>();
const odometerTensRef = ref<HTMLElement | null>(null);
const odometerOnesRef = ref<HTMLElement | null>(null);

const [sliderContainer, slider] = useKeenSlider<HTMLDivElement>({
    selector: ".odometer-value--unit",
    vertical: true,
    drag: false,
    mode: "free-snap",
    slides: {
        perView: 1,
    },
});

const getOnes = (val: number) => val % 10;
const getTens = (val: number) => Math.floor(val / 10);

const getValueAndUnit = (seconds: number): [number, number] => {
    const days = Math.floor(seconds / 86400);
    if (days > 0) {
        return [days, 3];
    }

    const time = new Date(seconds * 1000);

    const hours = time.getUTCHours();
    if (hours > 0) {
        return [hours, 2];
    }

    const minutes = time.getUTCMinutes();
    if (minutes > 0) {
        return [minutes, 1];
    }

    return [time.getUTCSeconds(), 0];
};

const timeSplitted = computed(() => {
    const [value, unit] = getValueAndUnit(props.remainingSeconds);

    return [getTens(value), getOnes(value), unit];
});

const odometerTens = computed(() => timeSplitted.value[0]);
const odometerOnes = computed(() => timeSplitted.value[1]);
const odometerUnit = computed(() => timeSplitted.value[2]);

let hasInitialSlidingCompleted = false;
const oneSlideChangeDuration = 240;
const slidesCount = 4;
const oneRoundAnimationSettings = computed(() => {
    return { duration: oneSlideChangeDuration * slidesCount, easing: (t) => (t -= 1) * t * t + 1 };
});

watch(odometerTens, (newValue) => {
    odometerTensRef.value.innerText = newValue.toString();
});
watch(odometerOnes, (newValue) => {
    odometerOnesRef.value.innerText = newValue.toString();
});
watch(odometerUnit, (newValue) => {
    if (hasInitialSlidingCompleted) {
        slider.value.moveToIdx(newValue, true, oneRoundAnimationSettings.value);
    }
});

onMounted(() => {
    onViewportEnter(bonusomatOdometer.value).then(() => {
        const odoTens = new Odometer({
            el: odometerTensRef.value,
            value: odometerTens.value,
            duration: 700,
        });

        const odoOnes = new Odometer({
            el: odometerOnesRef.value,
            value: odometerOnes.value,
            duration: 700,
        });

        odoTens.render();
        odoOnes.render();
    });

    setTimeout(() => {
        slider.value.moveToIdx(odometerUnit.value, true, oneRoundAnimationSettings.value);
        hasInitialSlidingCompleted = true;
    }, 2000);
});
</script>
